import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, Upload, DatePicker, message, Row, Col, Typography } from "antd";
import { UploadOutlined, DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment"; // ✅ Import moment to handle DatePicker format
import Axios from "../../../services/AxiosInterceptor";

const STORAGE_URL = process.env.REACT_APP_API_URL || "https://yogamatic.in/api/upload";

const { Option } = Select;
const { Text } = Typography;

const EditMembers = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [newMedicalReport, setNewMedicalReport] = useState(null); // For new file upload
  const navigate = useNavigate();
  const { id } = useParams();

  console.log("Member ID:", id);

  // ✅ Fetch Member Details by ID
  useEffect(() => {
    const fetchMember = async () => {
      try {
        const response = await Axios.post("api/biz_single_member_details", { id });

        if (response.data.success) {
          setMember(response.data.data);
        } else {
          message.error(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching member details:", error);
        message.error("Failed to fetch member details.");
      }
    };

    fetchMember();
  }, [id]);

  // ✅ Auto-fill form when member data is available
  useEffect(() => {
    if (member) {
      form.setFieldsValue({
        name: member.name,
        email: member.email,
        age: member.age,
        gender: member.gender,
        experience: member.experience,
        membership: member.membership,
        fee: member.fee,
        yogaType: member.yogaType,
        illness: member.illness,
        joiningDate: moment(member.joiningDate), // Convert to AntD DatePicker format
        Address: member.address, // Make sure `address` exists in your DB
      });
    }
  }, [member, form]);

  // ✅ Handle form submission (Update Member)
  const onFinish = async (values) => {
    try {
      setLoading(true);

      // ✅ Create FormData for file upload
      const formData = new FormData();
      formData.append("id", id);
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("age", values.age);
      formData.append("gender", values.gender);
      formData.append("experience", values.experience);
      formData.append("membership", values.membership);
      formData.append("fee", values.fee);
      formData.append("yogaType", values.yogaType);
      formData.append("illness", values.illness || "");
      formData.append("joiningDate", values.joiningDate.format("YYYY-MM-DD"));
      formData.append("Address", values.Address);

      // ✅ Attach new medical report if uploaded
      if (values.medicalReport?.length > 0) {
        formData.append("medicalReport", values.medicalReport[0].originFileObj);
      }

      // ✅ Send data to backend API for updating member details
      const response = await Axios.post("api/update_member", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        message.success("Member updated successfully! 🎉");
        navigate("/Member_List"); // Redirect to member list after update
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating member:", error);
      message.error("Update failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>🧘 Edit Member Details</h2>
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        {/* Row 1: Name, Email, Age */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Please enter member's name!" }]}>
              <Input placeholder="Enter full name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Email" name="email" rules={[
                { required: true, message: "Please enter email!" },
                { type: "email", message: "Enter a valid email!" },
              ]}>
              <Input placeholder="Enter email" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Age" name="age" rules={[{ required: true, message: "Please enter age!" }]}>
              <Input type="number" placeholder="Enter age" min={5} max={100} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Gender, Yoga Experience, Membership Duration */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Gender" name="gender" rules={[{ required: true, message: "Please select gender!" }]}>
              <Select placeholder="Select gender">
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Yoga Experience" name="experience" rules={[{ required: true, message: "Please select an option!" }]}>
              <Select placeholder="Select an option">
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Membership Duration" name="membership" rules={[{ required: true, message: "Select membership duration!" }]}>
              <Select placeholder="Select duration">
                <Option value="1_month">1 Month</Option>
                <Option value="2_months">2 Months</Option>
                <Option value="3_months">3 Months</Option>
                <Option value="6_months">6 Months</Option>
                <Option value="1_year">1 Year</Option>
                <Option value="2_year">2 Year</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Yoga Type, Illness, Fee */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Yoga Type" name="yogaType" rules={[{ required: true, message: "Please select yoga type!" }]}>
              <Select placeholder="Select yoga type">
                <Option value="hatha">Hatha Yoga</Option>
                <Option value="power">Power Yoga</Option>
                <Option value="ashtanga">Ashtanga Yoga</Option>
                <Option value="kundalini">Kundalini Yoga</Option>
                <Option value="therapy">Therapeutic Yoga</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Illness (if any)" name="illness">
              <Input placeholder="Mention illness if any" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Fee Paid (₹)" name="fee" rules={[{ required: true, message: "Please enter fee amount!" }]}>
              <Input type="number" placeholder="Enter amount" />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Address, Joining Date */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Address" name="Address" rules={[{ required: true, message: "Please enter Address" }]}>
              <Input placeholder="Enter Address" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Joining Date" name="joiningDate" rules={[{ required: true, message: "Please select joining date!" }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5: Medical Report Section */}
        <Row gutter={16} style={{ marginTop: "20px", padding: "15px", background: "#f9f9f9", borderRadius: "8px" }}>
          <Col span={24}>
            <h3 style={{ color: "#4CAF50", marginBottom: "15px" }}>📄 Medical Report</h3>
            
            {/* Show existing medical report if available */}
            {member?.medicalReportS3Key && (
              <div style={{ marginBottom: "15px", padding: "10px", background: "#fff", borderRadius: "5px" }}>
                <Text strong style={{ color: "#4CAF50" }}>Current Medical Report: </Text>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const downloadUrl = `${STORAGE_URL}/${member.medicalReportS3Key}`;
                    window.open(downloadUrl, '_blank');
                  }}
                  style={{ color: "#4CAF50" }}
                >
                  <FileTextOutlined /> Download Current Report (PDF)
                </Button>
              </div>
            )}

            {!member?.medicalReportS3Key && (
              <div style={{ marginBottom: "15px" }}>
                <Text type="secondary" italic>No medical report currently uploaded</Text>
              </div>
            )}

            {/* Upload new medical report */}
            <Form.Item
              label="Upload New Medical Report (Optional)"
              name="medicalReport"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
              extra="Upload a new PDF to replace the existing medical report"
            >
              <Upload beforeUpload={() => false} listType="picture" accept=".pdf" maxCount={1}>
                <Button icon={<UploadOutlined />}>
                  {member?.medicalReportS3Key ? "Replace Medical Report (PDF)" : "Upload Medical Report (PDF)"}
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        {/* Submit Button */}
        <Form.Item>
          <Button htmlType="submit" loading={loading} style={{ width: "25%", background: "#DBA400" }}>
            Update Member
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditMembers;
